import EChartsReact from "echarts-for-react";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { useStopCodingDispatch, useStopCodingSelector } from "../OI/redux/ReduxContext";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createStripedPattern } from "../../utils/chartPatterns";
import { useDarkMode } from "../../hooks/useDarkMode";
import { SECO_BLUE, SECO_BLUE_TINT_20, SECO_PURPLE, SECO_PURPLE_TINT_70 } from "./stopReasonColors";
import {
    Button,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverFooter,
    TeachingPopoverSurface,
    TeachingPopoverTitle,
    TeachingPopoverTrigger
} from "@fluentui/react-components";

type ChartMode = 'count' | 'duration';

type StopSummary = {
    count: number;
    otherCount: number;
    nonOtherCount: number;
    duration: number;
    otherDuration: number;
    nonOtherDuration: number;
};

type ChartRow = {
    label: string;
    nonOtherValue: number;
    otherValue: number;
    childGroupId: string;
    isPreviousWeek?: boolean;
};

type StopCodingDrillDownProps = {
    compareMode?: boolean;
};

type YearWeek = StopSummary & {
    week: number;
    dayKeys: string[];
};

type YearMonth = StopSummary & {
    month: number;
    name: string;
    weeks: YearWeek[];
};

type YearData = StopSummary & {
    year: number;
    months: YearMonth[];
    weeks: YearWeek[];
};

type AggregationData = {
    yearsData: YearData[];
    totalSummary: StopSummary;
    daySummaries: Map<string, StopSummary>;
    hourSummaries: Map<string, StopSummary>;
    normalizedStops: NormalizedStop[];
};

type NormalizedStop = {
    year: number;
    month: number;
    week: number;
    dayKey: string;
    hour: number;
    isOther: boolean;
    durationMinutes: number;
    timestamp: number;
};

const NOT_OTHER_LABEL = 'Klassificerat';
const OKLASSIFICERAT_LABEL = 'Oklassificerat';
const PREVIOUS_WEEK_NOT_OTHER_LABEL = 'Förra veckans klassificerade';
const PREVIOUS_WEEK_OKLASSIFICERAT_LABEL = 'Förra veckans oklassificerade';
const CLASSIFIED_COLOR = SECO_BLUE;
const PREVIOUS_WEEK_CLASSIFIED_COLOR = SECO_PURPLE;
const OKLASSIFICERAT_STRIPE_COLOR = SECO_BLUE;
const PREVIOUS_WEEK_OKLASSIFICERAT_STRIPE_COLOR = SECO_PURPLE_TINT_70;
const createLegendIcon = (svgMarkup: string) => `image://data:image/svg+xml;utf8,${encodeURIComponent(svgMarkup)}`;
const CLASSIFIED_LEGEND_ICON = createLegendIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><rect x="0.5" y="0.5" width="15" height="11" rx="2" fill="${CLASSIFIED_COLOR}" stroke="rgba(0,0,0,0.12)"/></svg>`
);
const OKLASSIFICERAT_LEGEND_ICON = createLegendIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><defs><pattern id="oklassificerat-stripes" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(135)"><rect width="6" height="6" fill="${OKLASSIFICERAT_STRIPE_COLOR}"/><line x1="0" y1="0" x2="0" y2="6" stroke="${SECO_BLUE_TINT_20}" stroke-width="3"/></pattern></defs><rect x="0.5" y="0.5" width="15" height="11" rx="2" fill="url(#oklassificerat-stripes)" stroke="rgba(0,0,0,0.12)"/></svg>`
);
const PREVIOUS_WEEK_CLASSIFIED_LEGEND_ICON = createLegendIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><rect x="0.5" y="0.5" width="15" height="11" rx="2" fill="${PREVIOUS_WEEK_CLASSIFIED_COLOR}" stroke="rgba(0,0,0,0.12)"/></svg>`
);
const PREVIOUS_WEEK_OKLASSIFICERAT_LEGEND_ICON = createLegendIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12"><defs><pattern id="previous-week-oklassificerat-stripes" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(135)"><rect width="6" height="6" fill="${PREVIOUS_WEEK_OKLASSIFICERAT_STRIPE_COLOR}"/><line x1="0" y1="0" x2="0" y2="6" stroke="${SECO_PURPLE}" stroke-width="3"/></pattern></defs><rect x="0.5" y="0.5" width="15" height="11" rx="2" fill="url(#previous-week-oklassificerat-stripes)" stroke="rgba(0,0,0,0.12)"/></svg>`
);
type LegendItemName =
    | typeof NOT_OTHER_LABEL
    | typeof OKLASSIFICERAT_LABEL
    | typeof PREVIOUS_WEEK_NOT_OTHER_LABEL
    | typeof PREVIOUS_WEEK_OKLASSIFICERAT_LABEL;

const getTopLegendConfig = (showPreviousWeekLegend: boolean) => ({
    top: -5,
    type: 'scroll' as const,
    orient: 'horizontal' as const,
    data: [
        {
            name: NOT_OTHER_LABEL,
            icon: CLASSIFIED_LEGEND_ICON
        },
        {
            name: OKLASSIFICERAT_LABEL,
            icon: OKLASSIFICERAT_LEGEND_ICON
        },
        ...(showPreviousWeekLegend ? [
            {
                name: PREVIOUS_WEEK_NOT_OTHER_LABEL,
                icon: PREVIOUS_WEEK_CLASSIFIED_LEGEND_ICON
            },
            {
                name: PREVIOUS_WEEK_OKLASSIFICERAT_LABEL,
                icon: PREVIOUS_WEEK_OKLASSIFICERAT_LEGEND_ICON
            }
        ] : [])
    ]
});

let persistedCustomRange = {
    from: '',
    to: ''
};

type LegendSelection = {
    [NOT_OTHER_LABEL]: boolean;
    [OKLASSIFICERAT_LABEL]: boolean;
    [PREVIOUS_WEEK_NOT_OTHER_LABEL]: boolean;
    [PREVIOUS_WEEK_OKLASSIFICERAT_LABEL]: boolean;
};

type BreadcrumbItem = {
    id: string;
    label: string;
};

type DefaultViewState = {
    id: string;
    stack: string[];
    breadcrumbs: BreadcrumbItem[];
};

type NavigationView = {
    id: string;
    stack: string[];
    breadcrumbs: BreadcrumbItem[];
};

type DrilldownChartProps = {
    chartRef: any;
    themedOption: any;
    chartEvents: Record<string, (params: any) => void>;
};

const DrilldownChart = memo(function DrilldownChart({
    chartRef,
    themedOption,
    chartEvents
}: DrilldownChartProps) {
    return (
        <EChartsReact
            ref={chartRef}
            style={{ height: 220, width: "100%" }}
            option={themedOption}
            onEvents={chartEvents}
            notMerge={true}
            lazyUpdate={true}
            opts={{ renderer: 'canvas' }}
        />
    );
});

const normalizeStopReason = (stopReason?: string) => {
    const normalizedReason = stopReason?.trim() || 'Unknown';
    const loweredReason = normalizedReason.toLowerCase();

    if (loweredReason === 'other' || loweredReason === 'oklassificerat' || normalizedReason.includes('PI Point not found')) {
        return OKLASSIFICERAT_LABEL;
    }

    return normalizedReason;
};

const isOtherStop = (stopReason?: string) => normalizeStopReason(stopReason) === OKLASSIFICERAT_LABEL;

const formatDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseDateKey = (dayKey: string) => new Date(`${dayKey}T00:00:00`);

const formatTooltipDateLabel = (date: Date) => `${date.toLocaleDateString('en-US', { weekday: 'long' })} ${formatDateKey(date)}`;

const formatWeekDayAxisLabel = (date: Date) => date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
});

const formatHourRange = (hour: number) => {
    const startHour = String(hour).padStart(2, '0');
    const endHour = String(hour + 1).padStart(2, '0');
    return `${startHour}:00 - ${endHour}:00`;
};

const getBreadcrumbLabelFromGroupId = (groupId: string, fallbackLabel: string) => {
    if (groupId.startsWith('day-')) {
        const dayKey = groupId.split('-').slice(3).join('-');
        return dayKey || fallbackLabel;
    }

    if (groupId.startsWith('custom-day-')) {
        return groupId.replace('custom-day-', '') || fallbackLabel;
    }

    return fallbackLabel;
};

const getBreadcrumbTrailKey = (items: BreadcrumbItem[]) => items.map(item => item.id).join('>');

const getMonthName = (year: number, month: number) => new Date(year, month).toLocaleString('en-US', { month: 'long' });

const findSequentialViewWithDifferentId = (
    views: NavigationView[],
    startIndex: number,
    currentId: string,
    step: -1 | 1
) => {
    for (let index = startIndex + step; index >= 0 && index < views.length; index += step) {
        if (views[index]?.id !== currentId) {
            return views[index];
        }
    }

    return undefined;
};

const formatDateTime = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}.${month}.${day} ${hours}:${minutes}`;
};

const formatRange = (start: Date, end: Date) => `${formatDateTime(start)} - ${formatDateTime(end)}`;

const formatDatePickerValue = (value: string) => {
    const date = parseDateTimeString(value);

    return date ? date.toLocaleDateString('en-US') : '';
};

const toDateBoundaryString = (date: Date | null | undefined, boundary: 'start' | 'end') => {
    if (!date) {
        return '';
    }

    const dateString = date.toLocaleDateString('sv-SE');
    return `${dateString}T${boundary === 'start' ? '00:00' : '23:59'}`;
};

const parseDateTimeString = (value: string): Date | undefined => {
    if (!value) {
        return undefined;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const getMondayBasedDayIndex = (date: Date) => (date.getDay() + 6) % 7;

const getWeek = (date: Date) => {
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);
    normalizedDate.setDate(normalizedDate.getDate() + 3 - getMondayBasedDayIndex(normalizedDate));

    const firstThursday = new Date(normalizedDate.getFullYear(), 0, 4);
    firstThursday.setHours(0, 0, 0, 0);
    firstThursday.setDate(firstThursday.getDate() + 3 - getMondayBasedDayIndex(firstThursday));

    return 1 + Math.round((normalizedDate.getTime() - firstThursday.getTime()) / (7 * 86400000));
};

const getStartOfWeek = (date: Date) => {
    const weekStart = new Date(date);
    weekStart.setHours(0, 0, 0, 0);
    weekStart.setDate(weekStart.getDate() - getMondayBasedDayIndex(weekStart));
    return weekStart;
};

const addDays = (date: Date, days: number) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
};

const getDaySummaryKey = (date: Date) => `${date.getFullYear()}-${getWeek(date)}-${formatDateKey(date)}`;

const getDayGroupId = (date: Date) => `day-${date.getFullYear()}-${getWeek(date)}-${formatDateKey(date)}`;

const getHourSummaryKey = (date: Date, hour: number) => `${getDaySummaryKey(date)}-${hour}`;

const getHourGroupId = (date: Date, hour: number) => `hour-${date.getFullYear()}-${getWeek(date)}-${formatDateKey(date)}-${hour}`;

const formatDurationMinutes = (minutes: number) => {
    const roundedMinutes = Math.round(minutes);
    const hours = Math.floor(roundedMinutes / 60);
    const remainingMinutes = roundedMinutes % 60;

    if (hours === 0) {
        return `${remainingMinutes}m`;
    }

    if (remainingMinutes === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${remainingMinutes}m`;
};

const formatDurationAxisHours = (minutes: number) => {
    if (minutes <= 0) {
        return '0h';
    }

    const roundedHours = Math.ceil(minutes / 60);

    if (roundedHours >= 1000) {
        const scaledHours = roundedHours / 1000;
        return `${scaledHours.toFixed(1).replace(/\.0$/, '')}K`;
    }

    return `${roundedHours}h`;
};

const formatCompactCountAxisValue = (value: number) => {
    const absoluteValue = Math.abs(value);

    if (absoluteValue < 1000) {
        return `${value}`;
    }

    const scaledValue = value / 1000;
    return `${scaledValue.toFixed(1).replace(/\.0$/, '')}K`;
};

const createEmptySummary = (): StopSummary => ({
    count: 0,
    otherCount: 0,
    nonOtherCount: 0,
    duration: 0,
    otherDuration: 0,
    nonOtherDuration: 0
});

const addNormalizedStopToSummary = (summary: StopSummary, stop: NormalizedStop) => {
    summary.count += 1;
    summary.duration += stop.durationMinutes;

    if (stop.isOther) {
        summary.otherCount += 1;
        summary.otherDuration += stop.durationMinutes;
    } else {
        summary.nonOtherCount += 1;
        summary.nonOtherDuration += stop.durationMinutes;
    }
};

const buildWeekComparisonRows = (
    dayKeys: string[],
    daySummaries: Map<string, StopSummary>,
    mode: ChartMode
) => dayKeys.flatMap((dayKey) => {
    const currentDate = parseDateKey(dayKey);
    const previousWeekDate = addDays(currentDate, -7);

    return [
        toChartRow(
            formatWeekDayAxisLabel(previousWeekDate),
            daySummaries.get(getDaySummaryKey(previousWeekDate)) || createEmptySummary(),
            getDayGroupId(previousWeekDate),
            mode,
            { isPreviousWeek: true }
        ),
        toChartRow(
            formatWeekDayAxisLabel(currentDate),
            daySummaries.get(getDaySummaryKey(currentDate)) || createEmptySummary(),
            getDayGroupId(currentDate),
            mode
        )
    ];
});

const getOrCreateSummary = (map: Map<string, StopSummary>, key: string) => {
    const existing = map.get(key);

    if (existing) {
        return existing;
    }

    const created = createEmptySummary();
    map.set(key, created);
    return created;
};

const toChartRow = (
    label: string,
    summary: StopSummary,
    childGroupId: string,
    mode: ChartMode,
    options?: { isPreviousWeek?: boolean }
): ChartRow => ({
    label,
    nonOtherValue: mode === 'count' ? summary.nonOtherCount : summary.nonOtherDuration,
    otherValue: mode === 'count' ? summary.otherCount : summary.otherDuration,
    childGroupId,
    isPreviousWeek: options?.isPreviousWeek
});

const getYAxisConfig = (mode: ChartMode) => (
    mode === 'count'
        ? {
            minInterval: 1,
            axisLabel: {
                formatter: (value: number) => formatCompactCountAxisValue(value)
            }
        }
        : {
            axisLabel: {
                formatter: (value: number) => formatDurationAxisHours(value)
            }
        }
);

const getTooltipFormatter = (mode: ChartMode) => (params: any) => {
    const items = Array.isArray(params) ? params : [params];
    const firstValue = items[0]?.value;
    const childGroupId = Array.isArray(firstValue) ? firstValue[3] : undefined;
    const axisLabel = (() => {
        if (typeof childGroupId === 'string' && childGroupId.startsWith('day-')) {
            const dayKey = childGroupId.split('-').slice(3).join('-');

            if (dayKey) {
                return formatTooltipDateLabel(parseDateKey(dayKey));
            }
        }

        if (typeof childGroupId === 'string' && childGroupId.startsWith('custom-day-')) {
            const dayKey = childGroupId.replace('custom-day-', '');

            if (dayKey) {
                return formatTooltipDateLabel(parseDateKey(dayKey));
            }
        }

        return items[0]?.axisValueLabel || items[0]?.name || '';
    })();

    const getTooltipMarker = (seriesName: string) => {
        if (seriesName === OKLASSIFICERAT_LABEL) {
            return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-image:repeating-linear-gradient(135deg, ${SECO_BLUE} 0 3px, ${SECO_BLUE_TINT_20} 3px 6px);border:1px solid rgba(0,0,0,0.12);"></span>`;
        }

        if (seriesName === PREVIOUS_WEEK_OKLASSIFICERAT_LABEL) {
            return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-image:repeating-linear-gradient(135deg, ${PREVIOUS_WEEK_OKLASSIFICERAT_STRIPE_COLOR} 0 3px, ${SECO_PURPLE} 3px 6px);border:1px solid rgba(0,0,0,0.12);"></span>`;
        }

        if (seriesName === PREVIOUS_WEEK_NOT_OTHER_LABEL) {
            return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${PREVIOUS_WEEK_CLASSIFIED_COLOR};border:1px solid rgba(0,0,0,0.12);"></span>`;
        }

        return `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${SECO_BLUE};border:1px solid rgba(0,0,0,0.12);"></span>`;
    };

    return [
        axisLabel,
        ...items.map((item: any) => {
            const value = Array.isArray(item.value) ? item.value[1] : item.value;
            return `${getTooltipMarker(item.seriesName)}${item.seriesName}: ${mode === 'duration' ? formatDurationMinutes(value) : value}`;
        })
    ].join('<br/>');
};

const formatVisibleStackValue = (
    row: ChartRow | undefined,
    mode: ChartMode,
    seriesName: LegendItemName,
    legendSelection: LegendSelection,
    classifiedLabel: LegendItemName,
    otherLabel: LegendItemName
) => {
    if (!row) {
        return '';
    }

    const isNotOtherSelected = legendSelection[classifiedLabel];
    const isOtherSelected = legendSelection[otherLabel];
    const nonOtherValue = row.nonOtherValue || 0;
    const otherValue = row.otherValue || 0;

    if (!isNotOtherSelected && !isOtherSelected) {
        return '';
    }

    let visibleValue: number | undefined;

    if (isNotOtherSelected && isOtherSelected) {
        visibleValue = seriesName === otherLabel ? nonOtherValue + otherValue : undefined;
    } else if (isNotOtherSelected) {
        visibleValue = seriesName === classifiedLabel ? nonOtherValue : undefined;
    } else if (isOtherSelected) {
        visibleValue = seriesName === otherLabel ? otherValue : undefined;
    }

    if (visibleValue === undefined) {
        return '';
    }

    if (visibleValue <= 0) {
        return '';
    }

    return mode === 'duration' ? formatDurationMinutes(visibleValue) : visibleValue;
};

const DRILLDOWN_BAR_MAX_WIDTH = 56;

type SeriesDefinition = {
    name: LegendItemName;
    isPreviousWeekSeries: boolean;
    isOtherSeries: boolean;
    getValue: (row: ChartRow) => number;
    classifiedLabel: LegendItemName;
    otherLabel: LegendItemName;
};

const CURRENT_WEEK_SERIES_DEFINITIONS: SeriesDefinition[] = [
    {
        name: NOT_OTHER_LABEL,
        isPreviousWeekSeries: false,
        isOtherSeries: false,
        getValue: (row) => row.nonOtherValue,
        classifiedLabel: NOT_OTHER_LABEL,
        otherLabel: OKLASSIFICERAT_LABEL
    },
    {
        name: OKLASSIFICERAT_LABEL,
        isPreviousWeekSeries: false,
        isOtherSeries: true,
        getValue: (row) => row.otherValue,
        classifiedLabel: NOT_OTHER_LABEL,
        otherLabel: OKLASSIFICERAT_LABEL
    }
];

const COMPARE_WEEK_SERIES_DEFINITIONS: SeriesDefinition[] = [
    {
        name: NOT_OTHER_LABEL,
        isPreviousWeekSeries: false,
        isOtherSeries: false,
        getValue: (row) => row.isPreviousWeek ? 0 : row.nonOtherValue,
        classifiedLabel: NOT_OTHER_LABEL,
        otherLabel: OKLASSIFICERAT_LABEL
    },
    {
        name: OKLASSIFICERAT_LABEL,
        isPreviousWeekSeries: false,
        isOtherSeries: true,
        getValue: (row) => row.isPreviousWeek ? 0 : row.otherValue,
        classifiedLabel: NOT_OTHER_LABEL,
        otherLabel: OKLASSIFICERAT_LABEL
    },
    {
        name: PREVIOUS_WEEK_NOT_OTHER_LABEL,
        isPreviousWeekSeries: true,
        isOtherSeries: false,
        getValue: (row) => row.isPreviousWeek ? row.nonOtherValue : 0,
        classifiedLabel: PREVIOUS_WEEK_NOT_OTHER_LABEL,
        otherLabel: PREVIOUS_WEEK_OKLASSIFICERAT_LABEL
    },
    {
        name: PREVIOUS_WEEK_OKLASSIFICERAT_LABEL,
        isPreviousWeekSeries: true,
        isOtherSeries: true,
        getValue: (row) => row.isPreviousWeek ? row.otherValue : 0,
        classifiedLabel: PREVIOUS_WEEK_NOT_OTHER_LABEL,
        otherLabel: PREVIOUS_WEEK_OKLASSIFICERAT_LABEL
    }
];

const shouldShowSeriesLabel = (
    seriesDefinition: SeriesDefinition,
    legendSelection: LegendSelection
) => {
    const isClassifiedSelected = legendSelection[seriesDefinition.classifiedLabel];
    const isOtherSelected = legendSelection[seriesDefinition.otherLabel];

    if (isClassifiedSelected && isOtherSelected) {
        return seriesDefinition.isOtherSeries;
    }

    if (isClassifiedSelected) {
        return !seriesDefinition.isOtherSeries;
    }

    if (isOtherSelected) {
        return seriesDefinition.isOtherSeries;
    }

    return false;
};

const buildStackedSeries = (
    rows: ChartRow[],
    groupId: string,
    stripedPattern: HTMLCanvasElement,
    previousWeekStripedPattern: HTMLCanvasElement,
    mode: ChartMode,
    legendSelection: LegendSelection,
    showValueLabels: boolean = true,
    showHoverValueLabels: boolean = false,
    showPreviousWeekSeries: boolean = false
) => {
    const seriesDefinitions = showPreviousWeekSeries ? COMPARE_WEEK_SERIES_DEFINITIONS : CURRENT_WEEK_SERIES_DEFINITIONS;

    return seriesDefinitions.map((seriesDefinition) => ({
        name: seriesDefinition.name,
        type: 'bar',
        stack: 'stops',
        dimensions: ['x', 'y', 'groupId', 'childGroupId'],
        encode: {
            x: 'x',
            y: 'y',
            itemGroupId: 'groupId',
            itemChildGroupId: 'childGroupId'
        },
        data: rows.map(row => [
            row.label,
            seriesDefinition.getValue(row),
            groupId,
            row.childGroupId
        ]),
        barMaxWidth: DRILLDOWN_BAR_MAX_WIDTH,
        itemStyle: {
            color: (params: any) => {
                if (seriesDefinition.isOtherSeries) {
                    return {
                        type: 'pattern',
                        image: seriesDefinition.isPreviousWeekSeries
                            ? previousWeekStripedPattern
                            : stripedPattern,
                        repeat: 'repeat'
                    };
                }

                return seriesDefinition.isPreviousWeekSeries
                    ? PREVIOUS_WEEK_CLASSIFIED_COLOR
                    : CLASSIFIED_COLOR;
            }
        },
        label: {
            show: showValueLabels && shouldShowSeriesLabel(seriesDefinition, legendSelection),
            position: 'top',
            color: '#ffffff',
            fontWeight: 'bold',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            padding: [4, 8],
            borderRadius: 4,
            formatter: (params: any) => formatVisibleStackValue(
                rows[params.dataIndex],
                mode,
                seriesDefinition.name,
                legendSelection,
                seriesDefinition.classifiedLabel,
                seriesDefinition.otherLabel
            )
        },
        emphasis: {
            label: {
                show: showHoverValueLabels,
                position: 'top',
                color: '#ffffff',
                fontWeight: 'bold',
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                padding: [4, 8],
                borderRadius: 4,
                formatter: (params: any) => formatVisibleStackValue(
                    rows[params.dataIndex],
                    mode,
                    seriesDefinition.name,
                    legendSelection,
                    seriesDefinition.classifiedLabel,
                    seriesDefinition.otherLabel
                )
            }
        },
        universalTransition: {
            enabled: true,
            divideShape: 'clone'
        }
    }));
};

function StopCodingDrillDown({ compareMode = false }: StopCodingDrillDownProps) {
    const dispatch = useStopCodingDispatch();
    const chartMode = useStopCodingSelector(state => state.chartMode);
    const allStopCauses = useStopCodingSelector(state => state.allStopCauses);
    const stopCodingFilterDisplayValue = useStopCodingSelector(state => state.stopCodingFilterDisplayValue);
    const stripedPattern = useMemo(() => createStripedPattern(OKLASSIFICERAT_STRIPE_COLOR), []);
    const previousWeekStripedPattern = useMemo(() => createStripedPattern(PREVIOUS_WEEK_OKLASSIFICERAT_STRIPE_COLOR), []);
    const [legendSelection, setLegendSelection] = useState<LegendSelection>({
        [NOT_OTHER_LABEL]: true,
        [OKLASSIFICERAT_LABEL]: true,
        [PREVIOUS_WEEK_NOT_OTHER_LABEL]: true,
        [PREVIOUS_WEEK_OKLASSIFICERAT_LABEL]: true
    });
    const [customFromDateTime, setCustomFromDateTime] = useState(() => persistedCustomRange.from);
    const [customToDateTime, setCustomToDateTime] = useState(() => persistedCustomRange.to);
    const [isCustomRangePopoverOpen, setIsCustomRangePopoverOpen] = useState(false);
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
        { id: 'years', label: 'All' }
    ]);
    const chartRef = useRef<any>(null);
    const optionStack = useRef<string[]>([]);
    const currentOptionId = useRef<string>('years');
    const goBackRef = useRef<() => void>(() => undefined);
    const hasDispatchedInitialDateRange = useRef(false);
    const hasAppliedDefaultView = useRef(false);
    const isDarkMode = useDarkMode();

    const aggregationData = useMemo<AggregationData>(() => {
        const scopedStopCauses = allStopCauses;

        const years = Array.from(new Set(scopedStopCauses.map(stop => new Date(stop.StartTime).getFullYear()))).sort((left, right) => left - right);
        const normalizedStops: NormalizedStop[] = scopedStopCauses.map(stop => {
            const date = new Date(stop.StartTime);

            return {
                year: date.getFullYear(),
                month: date.getMonth(),
                week: getWeek(date),
                dayKey: formatDateKey(date),
                hour: date.getHours(),
                isOther: isOtherStop(stop.MachineStopReason),
                durationMinutes: (stop.Duration?.totalMilliseconds || 0) / 1000 / 60,
                timestamp: date.getTime()
            };
        });

        const totalSummary = createEmptySummary();
        const yearSummaries = new Map<number, StopSummary>();
        const monthSummaries = new Map<string, StopSummary>();
        const weekSummaries = new Map<string, StopSummary>();
        const daySummaries = new Map<string, StopSummary>();
        const hourSummaries = new Map<string, StopSummary>();

        normalizedStops.forEach(stop => {
            addNormalizedStopToSummary(totalSummary, stop);

            const yearSummary = yearSummaries.get(stop.year) || createEmptySummary();
            yearSummaries.set(stop.year, yearSummary);
            addNormalizedStopToSummary(yearSummary, stop);

            addNormalizedStopToSummary(getOrCreateSummary(monthSummaries, `${stop.year}-${stop.month}`), stop);
            addNormalizedStopToSummary(getOrCreateSummary(weekSummaries, `${stop.year}-${stop.week}`), stop);
            addNormalizedStopToSummary(getOrCreateSummary(daySummaries, `${stop.year}-${stop.week}-${stop.dayKey}`), stop);
            addNormalizedStopToSummary(getOrCreateSummary(hourSummaries, `${stop.year}-${stop.week}-${stop.dayKey}-${stop.hour}`), stop);
        });

        const yearsData = years.map(year => {
            const startOfYear = new Date(year, 0, 1);
            const endOfYear = new Date(year, 11, 31);
            const yearDates: Date[] = [];

            for (let date = new Date(startOfYear); date <= endOfYear; date = addDays(date, 1)) {
                yearDates.push(new Date(date));
            }

            const weekDayKeys = new Map<number, string[]>();

            yearDates.forEach(date => {
                const week = getWeek(date);

                if (weekDayKeys.has(week)) {
                    return;
                }

                const weekStart = getStartOfWeek(date);

                weekDayKeys.set(
                    week,
                    Array.from({ length: 7 }, (_, offset) => formatDateKey(addDays(weekStart, offset)))
                );
            });

            const weeks: YearWeek[] = Array.from(weekDayKeys.entries())
                .sort(([left], [right]) => left - right)
                .map(([week, dayKeys]) => ({
                    week,
                    dayKeys,
                    ...(weekSummaries.get(`${year}-${week}`) || createEmptySummary())
                }));

            const weeksByNumber = new Map(weeks.map(week => [week.week, week]));

            return {
                year,
                ...(yearSummaries.get(year) || createEmptySummary()),
                months: Array.from({ length: 12 }, (_, month) => {
                    const monthWeeks = Array.from(new Set(
                        yearDates
                            .filter(date => date.getMonth() === month)
                            .map(date => getWeek(date))
                    ))
                        .sort((left, right) => left - right)
                        .map(week => weeksByNumber.get(week))
                        .filter((week): week is YearWeek => Boolean(week));

                    return {
                        month,
                        name: new Date(0, month).toLocaleString('en-US', { month: 'long' }),
                        ...(monthSummaries.get(`${year}-${month}`) || createEmptySummary()),
                        weeks: monthWeeks
                    };
                }),
                weeks
            } satisfies YearData;
        });

        return {
            yearsData,
            totalSummary,
            daySummaries,
            hourSummaries,
            normalizedStops
        };
    }, [allStopCauses]);

    const { yearsData, totalSummary, daySummaries, hourSummaries, normalizedStops } = aggregationData;

    const defaultView = useMemo<DefaultViewState>(() => {
        if (normalizedStops.length === 0) {
            return {
                id: 'years',
                stack: [],
                breadcrumbs: [{ id: 'years', label: 'All' }]
            };
        }

        const latestStop = normalizedStops.reduce((latest, current) => current.timestamp > latest.timestamp ? current : latest);
        const yearId = `year-${latestStop.year}`;
        const monthId = `month-${latestStop.year}-${latestStop.month}`;
        const weekId = `week-${latestStop.year}-${latestStop.week}`;
        const monthLabel = yearsData
            .find((yearData) => yearData.year === latestStop.year)
            ?.months.find((monthData) => monthData.month === latestStop.month)
            ?.name ?? new Date(0, latestStop.month).toLocaleString('default', { month: 'long' });

        return {
            id: weekId,
            stack: ['years', yearId, monthId],
            breadcrumbs: [
                { id: 'years', label: 'All' },
                { id: yearId, label: latestStop.year.toString() },
                { id: monthId, label: monthLabel },
                { id: weekId, label: `Week ${latestStop.week}` }
            ]
        };
    }, [normalizedStops, yearsData]);

    const customFromDate = useMemo(() => parseDateTimeString(customFromDateTime), [customFromDateTime]);
    const customToDate = useMemo(() => parseDateTimeString(customToDateTime), [customToDateTime]);
    const customRangeTriggerLabel = useMemo(() => {
        if (!customFromDateTime && !customToDateTime) {
            return 'Select custom date range';
        }

        const fromLabel = formatDatePickerValue(customFromDateTime) || 'Any';
        const toLabel = formatDatePickerValue(customToDateTime) || 'Any';
        return `${fromLabel} - ${toLabel}`;
    }, [customFromDateTime, customToDateTime]);
    const hasCustomDateRangeInput = Boolean(customFromDateTime || customToDateTime);
    const isCustomDateRangeValid = Boolean(customFromDate && customToDate && customFromDate <= customToDate);
    const isCustomDateRangeInvalid = Boolean(customFromDate && customToDate && customFromDate > customToDate);

    const customRangeData = useMemo(() => {
        if (!customFromDate || !customToDate || customFromDate > customToDate) {
            return {
                rows: [] as ChartRow[],
                summary: createEmptySummary(),
                daySummaries: new Map<string, StopSummary>()
            };
        }

        const summary = createEmptySummary();
        const customDaySummaries = new Map<string, StopSummary>();

        normalizedStops
            .filter((stop) => stop.timestamp >= customFromDate.getTime() && stop.timestamp <= customToDate.getTime())
            .forEach((stop) => {
                addNormalizedStopToSummary(summary, stop);
                addNormalizedStopToSummary(getOrCreateSummary(customDaySummaries, stop.dayKey), stop);
            });

        const dayRows: ChartRow[] = [];
        for (let date = new Date(customFromDate); date <= customToDate; date = addDays(date, 1)) {
            const dayKey = formatDateKey(date);
            dayRows.push(toChartRow(
                dayKey,
                customDaySummaries.get(dayKey) || createEmptySummary(),
                `custom-day-${dayKey}`,
                chartMode
            ));
        }

        return {
            rows: dayRows,
            summary,
            daySummaries: customDaySummaries
        };
    }, [chartMode, customFromDate, customToDate, normalizedStops]);

    const getGroupDateRange = useCallback((groupId: string) => {
        if (groupId === 'years') {
            if (allStopCauses.length === 0) {
                return undefined;
            }

            const timestamps = allStopCauses
                .map((stop) => new Date(stop.StartTime).getTime())
                .filter((value) => !Number.isNaN(value))
                .sort((left, right) => left - right);

            if (timestamps.length === 0) {
                return undefined;
            }

            const startDate = new Date(timestamps[0]);
            startDate.setHours(0, 0, 0, 0);

            const endDate = new Date(timestamps[timestamps.length - 1]);
            endDate.setHours(23, 59, 0, 0);

            return { startDate, endDate };
        }

        if (groupId.startsWith('year-')) {
            const year = Number(groupId.split('-')[1]);

            if (Number.isNaN(year)) {
                return undefined;
            }

            return {
                startDate: new Date(year, 0, 1, 0, 0),
                endDate: new Date(year, 11, 31, 23, 59)
            };
        }

        if (groupId.startsWith('month-')) {
            const [, yearValue, monthValue] = groupId.split('-');
            const year = Number(yearValue);
            const month = Number(monthValue);

            if (Number.isNaN(year) || Number.isNaN(month)) {
                return undefined;
            }

            return {
                startDate: new Date(year, month, 1, 0, 0),
                endDate: new Date(year, month + 1, 0, 23, 59)
            };
        }

        if (groupId.startsWith('week-')) {
            const [, yearValue, weekValue] = groupId.split('-');
            const year = Number(yearValue);
            const week = Number(weekValue);

            if (Number.isNaN(year) || Number.isNaN(week)) {
                return undefined;
            }

            const weekData = yearsData
                .find(yearData => yearData.year === year)
                ?.weeks.find(weekDataItem => weekDataItem.week === week);

            if (!weekData || weekData.dayKeys.length === 0) {
                return undefined;
            }

            const sortedDayKeys = [...weekData.dayKeys].sort();
            const startDate = new Date(`${sortedDayKeys[0]}T00:00:00`);
            const endDate = new Date(`${sortedDayKeys[sortedDayKeys.length - 1]}T23:59:00`);

            return { startDate, endDate };
        }

        if (groupId.startsWith('day-')) {
            const parts = groupId.split('-');
            const dayKey = parts.slice(3).join('-');

            if (!dayKey) {
                return undefined;
            }

            return {
                startDate: new Date(`${dayKey}T00:00:00`),
                endDate: new Date(`${dayKey}T23:59:00`)
            };
        }

        if (groupId.startsWith('hour-')) {
            const parts = groupId.split('-');
            const hour = Number(parts[parts.length - 1]);
            const dayKey = parts.slice(3, parts.length - 1).join('-');

            if (!dayKey || Number.isNaN(hour)) {
                return undefined;
            }

            const startDate = new Date(`${dayKey}T00:00:00`);
            startDate.setHours(hour, 0, 0, 0);

            const endDate = new Date(startDate);
            endDate.setHours(endDate.getHours() + 1, 0, 0, 0);

            return { startDate, endDate };
        }

        if (groupId.startsWith('custom-day-')) {
            const dayKey = groupId.replace('custom-day-', '');

            if (!dayKey) {
                return undefined;
            }

            const startDate = new Date(`${dayKey}T00:00:00`);
            const endDate = new Date(`${dayKey}T23:59:59`);

            if (customFromDate && startDate < customFromDate) {
                startDate.setTime(customFromDate.getTime());
            }

            if (customToDate && endDate > customToDate) {
                endDate.setTime(customToDate.getTime());
            }

            return { startDate, endDate };
        }

        if (groupId.startsWith('custom-hour-')) {
            const parts = groupId.split('-');
            const hour = Number(parts[parts.length - 1]);
            const dayKey = parts.slice(2, parts.length - 1).join('-');

            if (!dayKey || Number.isNaN(hour)) {
                return undefined;
            }

            const startDate = new Date(`${dayKey}T00:00:00`);
            startDate.setHours(hour, 0, 0, 0);

            const endDate = new Date(startDate);
            endDate.setHours(endDate.getHours() + 1, 0, 0, 0);

            if (customFromDate && startDate < customFromDate) {
                startDate.setTime(customFromDate.getTime());
            }

            if (customToDate && endDate > customToDate) {
                endDate.setTime(customToDate.getTime());
            }

            return { startDate, endDate };
        }

        return undefined;
    }, [allStopCauses, customFromDate, customToDate, yearsData]);

    const dispatchDateRange = useCallback((groupId: string) => {
        const range = getGroupDateRange(groupId);

        if (!range) {
            return;
        }

        dispatch({ type: 'SET_FIELD', payload: { field: 'startDate', value: range.startDate } });
        dispatch({ type: 'SET_FIELD', payload: { field: 'endDate', value: range.endDate } });
    }, [dispatch, getGroupDateRange]);

    const getClickedBarRange = useCallback((groupId: string) => {
        const range = getGroupDateRange(groupId);

        if (!range) {
            return undefined;
        }

        return formatRange(range.startDate, range.endDate);
    }, [getGroupDateRange]);

    const getThemedOption = useCallback((option: any) => {
        const axisUiColor = isDarkMode ? '#555' : '#ccc';

        return {
            ...option,
            textStyle: {
                color: isDarkMode ? '#ffffff' : '#2f2f2fff'
            },
            legend: {
                ...option.legend,
                selected: legendSelection,
                textStyle: {
                    color: isDarkMode ? '#ffffff' : '#2f2f2fff',
                    fontSize: 12
                },
                top: 5
            },
            grid: {
                left: 40,
                right: 20,
                bottom: 45,
                top: 40,
                containLabel: false
            },
            xAxis: {
                ...option.xAxis,
                axisLabel: {
                    fontSize: 12,
                    color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                },
                axisLine: {
                    lineStyle: {
                        color: axisUiColor
                    }
                },
                axisTick: {
                    lineStyle: {
                        color: axisUiColor
                    }
                }
            },
            yAxis: {
                ...option.yAxis,
                axisLabel: {
                    ...(option.yAxis?.axisLabel || {}),
                    fontSize: 12,
                    color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                },
                axisLine: {
                    lineStyle: {
                        color: axisUiColor
                    }
                },
                axisTick: {
                    lineStyle: {
                        color: axisUiColor
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: axisUiColor
                    }
                }
            }
        };
    }, [isDarkMode, legendSelection]);

    const { countOptions, durationOptions } = useMemo(() => {
        const buildAllOptions = (mode: ChartMode) => {
            const allOptions: Record<string, any> = {};

            const buildOption = (
                id: string,
                groupId: string,
                rows: ChartRow[],
                showBack: boolean,
                showValueLabels: boolean = true
            ) => ({
                id,
                legend: getTopLegendConfig(compareMode && id.startsWith('week-')),
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: getTooltipFormatter(mode)
                },
                xAxis: { type: 'category' },
                yAxis: getYAxisConfig(mode),
                animationDurationUpdate: 500,
                series: buildStackedSeries(
                    rows,
                    groupId,
                    stripedPattern,
                    previousWeekStripedPattern,
                    mode,
                    legendSelection,
                    showValueLabels,
                    false,
                    compareMode && id.startsWith('week-')
                ),
            });

            allOptions['years'] = buildOption(
                'years',
                'years',
                yearsData.map(year => toChartRow(year.year.toString(), year, `year-${year.year}`, mode)),
                false
            );

            yearsData.forEach(year => {
                allOptions[`year-${year.year}`] = buildOption(
                    `year-${year.year}`,
                    `year-${year.year}`,
                    year.months.map(month => toChartRow(month.name, month, `month-${year.year}-${month.month}`, mode)),
                    true
                );
            });

            yearsData.forEach(year => {
                year.months.forEach(month => {
                    allOptions[`month-${year.year}-${month.month}`] = buildOption(
                        `month-${year.year}-${month.month}`,
                        `month-${year.year}-${month.month}`,
                        month.weeks.map(week => toChartRow(`Week ${week.week}`, week, `week-${year.year}-${week.week}`, mode)),
                        true
                    );
                });
            });

            yearsData.forEach(year => {
                year.weeks.forEach(week => {
                    allOptions[`week-${year.year}-${week.week}`] = buildOption(
                        `week-${year.year}-${week.week}`,
                        `week-${year.year}-${week.week}`,
                        compareMode
                            ? buildWeekComparisonRows(week.dayKeys, daySummaries, mode)
                            : week.dayKeys.map(dayKey => toChartRow(
                                dayKey,
                                daySummaries.get(`${year.year}-${week.week}-${dayKey}`) || createEmptySummary(),
                                `day-${year.year}-${week.week}-${dayKey}`,
                                mode
                            )),
                        true
                    );
                });
            });

            yearsData.forEach(year => {
                year.weeks.forEach(week => {
                    week.dayKeys.forEach(dayKey => {
                        const dayDate = parseDateKey(dayKey);
                        const dayGroupId = getDayGroupId(dayDate);

                        allOptions[dayGroupId] = buildOption(
                            dayGroupId,
                            dayGroupId,
                            Array.from({ length: 24 }, (_, hour) => toChartRow(
                                formatHourRange(hour),
                                hourSummaries.get(getHourSummaryKey(dayDate, hour)) || createEmptySummary(),
                                getHourGroupId(dayDate, hour),
                                mode
                            )),
                            true
                        );
                    });
                });
            });

            return allOptions;
        };

        return {
            countOptions: buildAllOptions('count'),
            durationOptions: buildAllOptions('duration')
        };
    }, [compareMode, daySummaries, hourSummaries, legendSelection, previousWeekStripedPattern, stripedPattern, yearsData]);

    const activeOptions = chartMode === 'count' ? countOptions : durationOptions;
    const customRangeOptions = useMemo(() => {
        if (!isCustomDateRangeValid) {
            return undefined;
        }

        const options: Record<string, any> = {};

        options['custom-range'] = {
            id: 'custom-range',
            legend: getTopLegendConfig(false),
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: getTooltipFormatter(chartMode)
            },
            xAxis: {
                type: 'category',
                axisLabel: {
                    interval: 0,
                    rotate: customRangeData.rows.length > 10 ? 40 : 0
                }
            },
            yAxis: getYAxisConfig(chartMode),
            animationDurationUpdate: 500,
            dataZoom: customRangeData.rows.length > 0 ? [
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    startValue: 0,
                    endValue: customRangeData.rows.length - 1,
                    filterMode: 'filter'
                },
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    bottom: 0,
                    height: 18,
                    startValue: 0,
                    endValue: customRangeData.rows.length - 1,
                    filterMode: 'filter',
                    brushSelect: false,
                    fillerColor: 'rgba(0, 108, 234, 0.18)',
                    borderColor: isDarkMode ? '#555' : '#d1d5db',
                    handleStyle: {
                        color: SECO_BLUE
                    },
                    textStyle: {
                        color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                    }
                }
            ] : undefined,
            series: buildStackedSeries(customRangeData.rows, 'custom-range', stripedPattern, previousWeekStripedPattern, chartMode, legendSelection, false, true),
            graphic: []
        };

        customRangeData.rows.forEach((row) => {
            const dayKey = row.childGroupId.replace('custom-day-', '');
            const hourRows = Array.from({ length: 24 }, (_, hour) => {
                const hourSummary = normalizedStops
                    .filter((stop) => stop.dayKey === dayKey && stop.hour === hour)
                    .filter((stop) => stop.timestamp >= customFromDate!.getTime() && stop.timestamp <= customToDate!.getTime())
                    .reduce((summary, stop) => {
                        addNormalizedStopToSummary(summary, stop);
                        return summary;
                    }, createEmptySummary());

                return toChartRow(
                    formatHourRange(hour),
                    hourSummary,
                    `custom-hour-${dayKey}-${hour}`,
                    chartMode
                );
            });

            options[row.childGroupId] = {
                id: row.childGroupId,
                legend: getTopLegendConfig(false),
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: getTooltipFormatter(chartMode)
                },
                xAxis: { type: 'category' },
                yAxis: getYAxisConfig(chartMode),
                animationDurationUpdate: 500,
                dataZoom: hourRows.length > 0 ? [
                    {
                        type: 'inside',
                        xAxisIndex: 0,
                        startValue: 0,
                        endValue: hourRows.length - 1,
                        filterMode: 'filter'
                    },
                    {
                        type: 'slider',
                        xAxisIndex: 0,
                        bottom: 45,
                        height: 18,
                        startValue: 0,
                        endValue: hourRows.length - 1,
                        filterMode: 'filter',
                        brushSelect: false,
                        fillerColor: 'rgba(0, 108, 234, 0.18)',
                        borderColor: isDarkMode ? '#555' : '#d1d5db',
                        handleStyle: {
                            color: SECO_BLUE
                        },
                        textStyle: {
                            color: isDarkMode ? '#ffffff' : '#2f2f2fff'
                        }
                    }
                ] : undefined,
                series: buildStackedSeries(hourRows, row.childGroupId, stripedPattern, previousWeekStripedPattern, chartMode, legendSelection, false, true),

            };
        });

        return options;
    }, [chartMode, customFromDate, customRangeData.rows, customToDate, isCustomDateRangeValid, isDarkMode, legendSelection, normalizedStops, previousWeekStripedPattern, stripedPattern]);

    const displayedOptions = customRangeOptions ?? activeOptions;
    const currentViewId = breadcrumbs[breadcrumbs.length - 1]?.id ?? currentOptionId.current;
    const displayedOption = displayedOptions[currentViewId] ?? displayedOptions[isCustomDateRangeValid ? 'custom-range' : 'years'];
    const currentSummary = useMemo(() => {
        if (currentViewId === 'years') {
            return totalSummary;
        }

        if (currentViewId === 'custom-range') {
            return customRangeData.summary;
        }

        if (currentViewId.startsWith('year-')) {
            const year = Number(currentViewId.split('-')[1]);
            return yearsData.find((yearData) => yearData.year === year) || createEmptySummary();
        }

        if (currentViewId.startsWith('month-')) {
            const [, yearValue, monthValue] = currentViewId.split('-');
            const year = Number(yearValue);
            const month = Number(monthValue);

            return yearsData
                .find((yearData) => yearData.year === year)
                ?.months.find((monthData) => monthData.month === month) || createEmptySummary();
        }

        if (currentViewId.startsWith('week-')) {
            const [, yearValue, weekValue] = currentViewId.split('-');
            const year = Number(yearValue);
            const week = Number(weekValue);

            return yearsData
                .find((yearData) => yearData.year === year)
                ?.weeks.find((weekData) => weekData.week === week) || createEmptySummary();
        }

        if (currentViewId.startsWith('day-')) {
            const parts = currentViewId.split('-');
            const year = Number(parts[1]);
            const week = Number(parts[2]);
            const dayKey = parts.slice(3).join('-');

            return daySummaries.get(`${year}-${week}-${dayKey}`) || createEmptySummary();
        }

        if (currentViewId.startsWith('hour-')) {
            const parts = currentViewId.split('-');
            const year = Number(parts[1]);
            const week = Number(parts[2]);
            const hour = Number(parts[parts.length - 1]);
            const dayKey = parts.slice(3, parts.length - 1).join('-');

            return hourSummaries.get(`${year}-${week}-${dayKey}-${hour}`) || createEmptySummary();
        }

        if (currentViewId.startsWith('custom-day-')) {
            const dayKey = currentViewId.replace('custom-day-', '');
            return customRangeData.daySummaries.get(dayKey) || createEmptySummary();
        }

        if (currentViewId.startsWith('custom-hour-')) {
            const parts = currentViewId.split('-');
            const hour = Number(parts[parts.length - 1]);
            const dayKey = parts.slice(2, parts.length - 1).join('-');

            return normalizedStops
                .filter((stop) => stop.dayKey === dayKey && stop.hour === hour)
                .filter((stop) => stop.timestamp >= (customFromDate?.getTime() ?? Number.NEGATIVE_INFINITY) && stop.timestamp <= (customToDate?.getTime() ?? Number.POSITIVE_INFINITY))
                .reduce((summary, stop) => {
                    addNormalizedStopToSummary(summary, stop);
                    return summary;
                }, createEmptySummary());
        }

        return isCustomDateRangeValid ? customRangeData.summary : totalSummary;
    }, [currentViewId, customFromDate, customRangeData.daySummaries, customRangeData.summary, customToDate, daySummaries, hourSummaries, isCustomDateRangeValid, normalizedStops, totalSummary, yearsData]);
    const sequentialNavigation = useMemo(() => {
        const rootBreadcrumb: BreadcrumbItem = { id: 'years', label: 'All' };
        const yearViews: NavigationView[] = yearsData.map((yearData) => ({
            id: `year-${yearData.year}`,
            stack: ['years'],
            breadcrumbs: [
                rootBreadcrumb,
                { id: `year-${yearData.year}`, label: yearData.year.toString() }
            ]
        }));

        const monthViews: NavigationView[] = yearsData.flatMap((yearData) => yearData.months.map((monthData) => ({
            id: `month-${yearData.year}-${monthData.month}`,
            stack: ['years', `year-${yearData.year}`],
            breadcrumbs: [
                rootBreadcrumb,
                { id: `year-${yearData.year}`, label: yearData.year.toString() },
                { id: `month-${yearData.year}-${monthData.month}`, label: monthData.name }
            ]
        })));

        const weekViews: NavigationView[] = yearsData.flatMap((yearData) => yearData.months.flatMap((monthData) => monthData.weeks.map((weekData) => ({
            id: `week-${yearData.year}-${weekData.week}`,
            stack: ['years', `year-${yearData.year}`, `month-${yearData.year}-${monthData.month}`],
            breadcrumbs: [
                rootBreadcrumb,
                { id: `year-${yearData.year}`, label: yearData.year.toString() },
                { id: `month-${yearData.year}-${monthData.month}`, label: monthData.name },
                { id: `week-${yearData.year}-${weekData.week}`, label: `Week ${weekData.week}` }
            ]
        }))));

        const uniqueDayKeys = Array.from(new Set(
            yearsData.flatMap((yearData) => yearData.weeks.flatMap((weekData) => weekData.dayKeys))
        )).sort();

        const dayViews: NavigationView[] = uniqueDayKeys.map((dayKey) => {
            const date = parseDateKey(dayKey);
            const year = date.getFullYear();
            const month = date.getMonth();
            const week = getWeek(date);

            return {
                id: getDayGroupId(date),
                stack: ['years', `year-${year}`, `month-${year}-${month}`, `week-${year}-${week}`],
                breadcrumbs: [
                    rootBreadcrumb,
                    { id: `year-${year}`, label: year.toString() },
                    { id: `month-${year}-${month}`, label: getMonthName(year, month) },
                    { id: `week-${year}-${week}`, label: `Week ${week}` },
                    { id: getDayGroupId(date), label: dayKey }
                ]
            };
        });

        const customDayViews: NavigationView[] = customRangeData.rows.map((row) => ({
            id: row.childGroupId,
            stack: ['custom-range'],
            breadcrumbs: [
                { id: 'custom-range', label: 'Custom Range' },
                { id: row.childGroupId, label: row.childGroupId.replace('custom-day-', '') }
            ]
        }));

        return {
            yearViews,
            monthViews,
            weekViews,
            dayViews,
            customDayViews
        };
    }, [customRangeData.rows, yearsData]);

    const activeSequentialViews = useMemo(() => {
        const currentId = currentViewId;

        if (currentId.startsWith('custom-day-')) {
            return sequentialNavigation.customDayViews;
        }

        if (currentId.startsWith('day-')) {
            return sequentialNavigation.dayViews;
        }

        if (currentId.startsWith('week-')) {
            return sequentialNavigation.weekViews;
        }

        if (currentId.startsWith('month-')) {
            return sequentialNavigation.monthViews;
        }

        if (currentId.startsWith('year-')) {
            return sequentialNavigation.yearViews;
        }

        return [] as NavigationView[];
    }, [currentViewId, sequentialNavigation]);

    const activeSequentialIndex = useMemo(() => {
        const breadcrumbTrailKey = getBreadcrumbTrailKey(breadcrumbs);
        const exactMatchIndex = activeSequentialViews.findIndex((view) => (
            view.id === currentViewId && getBreadcrumbTrailKey(view.breadcrumbs) === breadcrumbTrailKey
        ));

        if (exactMatchIndex >= 0) {
            return exactMatchIndex;
        }

        return activeSequentialViews.findIndex((view) => view.id === currentViewId);
    }, [activeSequentialViews, breadcrumbs, currentViewId]);

    const previousSequentialView = useMemo(() => {
        if (activeSequentialIndex < 0) {
            return undefined;
        }

        return findSequentialViewWithDifferentId(activeSequentialViews, activeSequentialIndex, currentViewId, -1);
    }, [activeSequentialIndex, activeSequentialViews, currentViewId]);

    const nextSequentialView = useMemo(() => {
        if (activeSequentialIndex < 0) {
            return undefined;
        }

        return findSequentialViewWithDifferentId(activeSequentialViews, activeSequentialIndex, currentViewId, 1);
    }, [activeSequentialIndex, activeSequentialViews, currentViewId]);
    // ---------- Navigation ----------

    const navigateToView = useCallback((targetView: NavigationView) => {
        const chart = chartRef.current?.getEchartsInstance?.();

        if (!displayedOptions[targetView.id]) {
            return;
        }

        optionStack.current = [...targetView.stack];
        currentOptionId.current = targetView.id;
        setBreadcrumbs(targetView.breadcrumbs);
        dispatchDateRange(targetView.id);

        if (!chart) {
            return;
        }

        chart.setOption(getThemedOption(displayedOptions[targetView.id]), true);
    }, [dispatchDateRange, displayedOptions, getThemedOption]);

    const goForward = useCallback((nextId: string, label: string) => {
        const chart = chartRef.current?.getEchartsInstance?.();

        if (!chart || !displayedOptions[nextId]) {
            return;
        }

        optionStack.current.push(currentOptionId.current);
        currentOptionId.current = nextId;
        setBreadcrumbs((currentBreadcrumbs) => {
            const nextBreadcrumbs = currentBreadcrumbs.filter((item) => item.id !== nextId);
            return [...nextBreadcrumbs, { id: nextId, label }];
        });
        chart.setOption(getThemedOption(displayedOptions[nextId]), true);
    }, [displayedOptions, getThemedOption]);

    const goBack = useCallback(() => {
        const chart = chartRef.current?.getEchartsInstance?.();
        const prev = optionStack.current.pop();

        if (!chart || !prev) {
            return;
        }

        currentOptionId.current = prev;
        setBreadcrumbs((currentBreadcrumbs) => currentBreadcrumbs.slice(0, -1));
        dispatchDateRange(prev);
        if (!displayedOptions[prev]) {
            return;
        }

        chart.setOption(getThemedOption(displayedOptions[prev]), true);
    }, [dispatchDateRange, displayedOptions, getThemedOption]);

    const goToBreadcrumb = useCallback((breadcrumbId: string, breadcrumbIndex: number) => {
        const chart = chartRef.current?.getEchartsInstance?.();

        if (!chart || currentOptionId.current === breadcrumbId || !displayedOptions[breadcrumbId]) {
            return;
        }

        optionStack.current = optionStack.current.slice(0, Math.max(0, breadcrumbIndex));
        currentOptionId.current = breadcrumbId;
        setBreadcrumbs((currentBreadcrumbs) => currentBreadcrumbs.slice(0, breadcrumbIndex + 1));
        dispatchDateRange(breadcrumbId);
        chart.setOption(getThemedOption(displayedOptions[breadcrumbId]), true);
    }, [dispatchDateRange, displayedOptions, getThemedOption]);

    goBackRef.current = goBack;

    useEffect(() => {
        if (hasDispatchedInitialDateRange.current || isCustomDateRangeValid) {
            return;
        }

        const range = getGroupDateRange(defaultView.id);

        if (!range) {
            return;
        }

        dispatch({ type: 'SET_FIELD', payload: { field: 'startDate', value: range.startDate } });
        dispatch({ type: 'SET_FIELD', payload: { field: 'endDate', value: range.endDate } });
        hasDispatchedInitialDateRange.current = true;
    }, [defaultView.id, dispatch, getGroupDateRange, isCustomDateRangeValid]);

    useEffect(() => {
        if (isCustomDateRangeValid && customFromDate && customToDate) {
            dispatch({ type: 'SET_FIELD', payload: { field: 'startDate', value: customFromDate } });
            dispatch({ type: 'SET_FIELD', payload: { field: 'endDate', value: customToDate } });
        }
    }, [customFromDate, customToDate, dispatch, isCustomDateRangeValid]);

    useEffect(() => {
        persistedCustomRange = {
            from: customFromDateTime,
            to: customToDateTime
        };
    }, [customFromDateTime, customToDateTime]);

    useEffect(() => {
        if (!isCustomDateRangeValid) {
            return;
        }

        optionStack.current = [];
        currentOptionId.current = 'custom-range';
        setBreadcrumbs([{ id: 'custom-range', label: 'Custom Range' }]);
    }, [customFromDateTime, customToDateTime, isCustomDateRangeValid]);

    useEffect(() => {
        if (hasAppliedDefaultView.current || isCustomDateRangeValid) {
            return;
        }

        optionStack.current = [...defaultView.stack];
        currentOptionId.current = defaultView.id;
        setBreadcrumbs(defaultView.breadcrumbs);
        hasAppliedDefaultView.current = true;
    }, [defaultView, isCustomDateRangeValid]);

    useEffect(() => {
        if (!displayedOptions[currentOptionId.current]) {
            optionStack.current = [];
            if (isCustomDateRangeValid) {
                currentOptionId.current = 'custom-range';
                setBreadcrumbs([{ id: 'custom-range', label: 'Custom Range' }]);
            } else {
                optionStack.current = [...defaultView.stack];
                currentOptionId.current = defaultView.id;
                setBreadcrumbs(defaultView.breadcrumbs);
            }
        }

        const chart = chartRef.current?.getEchartsInstance?.();

        if (!chart) {
            return;
        }

        if (!displayedOption) {
            return;
        }

        chart.setOption(getThemedOption(displayedOption), true);
    }, [defaultView, displayedOption, displayedOptions, getThemedOption, isCustomDateRangeValid]);

    useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        const chartInstance = chartRef.current.getEchartsInstance();
        chartInstance?.resize();
    }, [isDarkMode]);

    useEffect(() => {
        const handleResize = () => {
            chartRef.current?.getEchartsInstance?.()?.resize?.();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const totalLabel = chartMode === 'count'
        ? `STOPS: ${currentSummary.count}`
        : `DURATION: ${formatDurationMinutes(currentSummary.duration)}`;
    const selectedFilterLabel = stopCodingFilterDisplayValue || 'All';
    const filterLabel = ` ${selectedFilterLabel}`;

    const themedOption = useMemo(() => {
        return displayedOption ? getThemedOption(displayedOption) : undefined;
    }, [displayedOption, getThemedOption]);

    const handleLegendSelectChanged = useCallback((params: any) => {
        setLegendSelection({
            [NOT_OTHER_LABEL]: params.selected?.[NOT_OTHER_LABEL] !== false,
            [OKLASSIFICERAT_LABEL]: params.selected?.[OKLASSIFICERAT_LABEL] !== false,
            [PREVIOUS_WEEK_NOT_OTHER_LABEL]: params.selected?.[PREVIOUS_WEEK_NOT_OTHER_LABEL] !== false,
            [PREVIOUS_WEEK_OKLASSIFICERAT_LABEL]: params.selected?.[PREVIOUS_WEEK_OKLASSIFICERAT_LABEL] !== false
        });
    }, []);

    const handleChartClick = useCallback((params: any) => {
        const data = params.data;

        if (!data) {
            return;
        }

        const value = Array.isArray(data)
            ? Number(data[1] ?? 0)
            : Number(params.value ?? 0);

        if (!Number.isFinite(value) || value <= 0) {
            return;
        }

        const nextId = data[3];

        if (typeof nextId === 'string') {
            dispatchDateRange(nextId);
        }

        const clickedRange = typeof nextId === 'string' ? getClickedBarRange(nextId) : undefined;
        if (clickedRange) {
            console.log(clickedRange);
        }

        if (nextId && displayedOptions[nextId]) {
            goForward(nextId, getBreadcrumbLabelFromGroupId(nextId, String(params.name ?? nextId)));
        }
    }, [dispatchDateRange, displayedOptions, getClickedBarRange, goForward]);

    const goToPreviousSequentialView = useCallback(() => {
        if (!previousSequentialView) {
            return;
        }

        navigateToView(previousSequentialView);
    }, [navigateToView, previousSequentialView]);

    const goToNextSequentialView = useCallback(() => {
        if (!nextSequentialView) {
            return;
        }

        navigateToView(nextSequentialView);
    }, [navigateToView, nextSequentialView]);

    const clearCustomRange = useCallback(() => {
        setCustomFromDateTime('');
        setCustomToDateTime('');
        setIsCustomRangePopoverOpen(false);
        dispatchDateRange(currentOptionId.current);
    }, [dispatchDateRange]);

    const chartEvents = useMemo(() => ({
        legendselectchanged: handleLegendSelectChanged,
        click: handleChartClick
    }), [handleChartClick, handleLegendSelectChanged]);


    return (
        <div className="flex w-full flex-col">

            <div className="border-8 border-white dark:border-primary-dark p-4">
                <div className="mx-1 flex flex-col items-start">
                    <h2 className="text-lg font-semibold">
                        <span>{`${filterLabel} ${totalLabel}`}</span>
                    </h2>

                </div>


                {isCustomDateRangeInvalid && (
                    <div className="px-6 pb-2 text-sm text-red-600 dark:text-red-400">
                        From must be before To.
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3 px-5 text-sm text-slate-600 dark:text-slate-300">
                    <div className="flex flex-wrap items-center">
                        {breadcrumbs.map((item, index) => {
                            const isLastItem = index === breadcrumbs.length - 1;

                            return (
                                <span key={item.id}>
                                    <button
                                        type="button"
                                        className={isLastItem
                                            ? "cursor-default font-medium text-slate-800 dark:text-slate-100"
                                            : "cursor-pointer underline underline-offset-2"
                                        }
                                        onClick={() => goToBreadcrumb(item.id, index)}
                                        disabled={isLastItem}
                                    >
                                        {item.label}
                                    </button>
                                    {!isLastItem && <span className="px-2 text-slate-400">/</span>}
                                </span>
                            );
                        })}
                    </div>
                    <div className="flex items-center gap-2 h-8 ">
                        {activeSequentialViews.length > 0 && (

                            <>
                                <Button appearance="secondary" onClick={goToPreviousSequentialView} disabled={!previousSequentialView} style={{ borderRadius: 0 }}>
                                    Previous
                                </Button>
                                <Button appearance="secondary" onClick={goToNextSequentialView} disabled={!nextSequentialView} style={{ borderRadius: 0 }}>
                                    Next
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                {themedOption && (
                    <DrilldownChart
                        chartRef={chartRef}
                        themedOption={themedOption}
                        chartEvents={chartEvents}
                    />
                )}

                <div className="mt-4 flex flex-wrap items-end gap-4 px-6">
                    <div className="flex flex-1 justify-end ">
                        <TeachingPopover
                            open={isCustomRangePopoverOpen}
                            onOpenChange={(_, data) => setIsCustomRangePopoverOpen(data.open)}
                            positioning="below-start"
                            trapFocus

                        >
                            <TeachingPopoverTrigger>
                                <Button appearance="secondary" className="justify-end text-left" style={{ borderRadius: 0 }}>
                                    {customRangeTriggerLabel}
                                </Button>
                            </TeachingPopoverTrigger>
                            <TeachingPopoverSurface>
                                <TeachingPopoverBody>
                                    <TeachingPopoverTitle>Custom date range</TeachingPopoverTitle>
                                    <div className="mt-4 flex min-w-[320px] flex-col gap-4">
                                        <div className="flex flex-col gap-2">
                                            <h6>From</h6>
                                            <DatePicker
                                                placeholder={new Date().toLocaleDateString("sv-se")}
                                                value={customFromDate ?? null}
                                                onSelectDate={(date) => setCustomFromDateTime(toDateBoundaryString(date, 'start'))}
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <h6>To</h6>
                                            <DatePicker
                                                placeholder={new Date().toLocaleDateString("sv-se")}
                                                value={customToDate ?? null}
                                                onSelectDate={(date) => setCustomToDateTime(toDateBoundaryString(date, 'end'))}
                                            />
                                        </div>
                                    </div>
                                </TeachingPopoverBody>
                                <TeachingPopoverFooter
                                    primary={{
                                        children: 'Done',
                                        onClick: () => setIsCustomRangePopoverOpen(false)
                                    }}
                                    secondary={{
                                        children: 'Clear',
                                        onClick: clearCustomRange,
                                        disabled: !hasCustomDateRangeInput
                                    }}
                                />
                            </TeachingPopoverSurface>
                        </TeachingPopover>
                    </div>
                    <div className="pb-">
                        <Button appearance="secondary" onClick={clearCustomRange} disabled={!hasCustomDateRangeInput} style={{ borderRadius: 0 }}>
                            Clear
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(StopCodingDrillDown);
